import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getActiveDateFormat, resolveDateFormat, setActiveDateFormat } from '../utils/dateFormat';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// A login token lasts 24 hours. The server answers an expired one with 403 on every call, which
// used to leave screens full of "Failed to load" messages until the person signed out by hand.
function tokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch (e) {
    return false;
  }
}

function applySession(token, userData) {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(userData));
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  if (userData?.organizationId) {
    axios.defaults.headers.common['X-Tenant-ID'] = userData.organizationId;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // How dates look: the organization's own choice, else its country's usual one (utils/dateFormat.js)
  const [dateFormat, setDateFormat] = useState(getActiveDateFormat());
  // Whether the organization keeps expiry dates on its stock (Org settings); when it does not, nothing about expiry shows
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [priceListsEnabled, setPriceListsEnabled] = useState(false);
  // False until the organization's settings (date format, switches) have been read, so a screen can wait before judging a switch to be off
  const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);
  // Whether the organization sells online (Org Admin > Online Store); when it does not, the Online Store module is not in the menu
  const [storeEnabled, setStoreEnabled] = useState(false);
  const [storeNewOrders, setStoreNewOrders] = useState(0); // online orders waiting to be confirmed (only the people who work them are told)

  const refreshStore = useCallback(async () => {
    try {
      const store = await axios.get('/api/store/status');
      setStoreEnabled(Boolean(store.data?.enabled));
      setStoreNewOrders(Number(store.data?.newOrders) || 0);
    } catch (e) {
      setStoreEnabled(false);
      setStoreNewOrders(0);
    }
  }, []);

  const refreshDateFormat = useCallback(async (organizationId) => {
    if (!organizationId) return;
    try {
      const response = await axios.get(`/api/organizations/${organizationId}`);
      const format = resolveDateFormat(response.data?.dateFormat, response.data?.country);
      setActiveDateFormat(format);
      setDateFormat(format);
      setExpiryEnabled(Boolean(response.data?.expiryEnabled));
      setPriceListsEnabled(Boolean(response.data?.priceListsEnabled));
      setOrgSettingsLoaded(true);
      await refreshStore();
    } catch (e) {
      // Dates keep the format they had
      setOrgSettingsLoaded(true);
    }
  }, [refreshStore]);

  useEffect(() => {
    if (user?.organizationId) refreshDateFormat(user.organizationId);
  }, [user?.organizationId, refreshDateFormat]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && tokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } else if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (parsedUser?.organizationId) {
        axios.defaults.headers.common['X-Tenant-ID'] = parsedUser.organizationId;
      }
    }

    setLoading(false);

    // Signed in when the page opened but the token ran out while it stayed open: back to sign-in.
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const current = localStorage.getItem('token');
        const status = error.response?.status;
        if ((status === 401 || status === 403) && current && tokenExpired(current)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          delete axios.defaults.headers.common['X-Tenant-ID'];
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // A browser the person chose to remember after a correct code keeps its token here, so the code is not asked again for 30 days
  const trustedKey = (email) => `ibigdo.trusted.${String(email || '').trim().toLowerCase()}`;
  const readTrusted = (email) => {
    try { return localStorage.getItem(trustedKey(email)) || undefined; } catch (e) { return undefined; }
  };
  const keepTrusted = (email, token) => {
    try { if (token) localStorage.setItem(trustedKey(email), token); } catch (e) { /* the code is asked again */ }
  };

  const login = async (email, password, organizationId) => {
    try {
      const payload = { email, password, trustedBrowser: readTrusted(email) };
      if (organizationId) {
        payload.organizationId = organizationId;
      }
      const response = await axios.post('/api/auth/login', payload);
      // Password right, but a second step comes first: a code from the app, or setting two-step up when the organization requires it
      if (response.data?.twoFactorRequired || response.data?.twoFactorSetupRequired) {
        return { success: false, twoFactor: response.data };
      }
      const { token, user: userData } = response.data;
      applySession(token, userData);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Login failed'
      };
    }
  };

  // The steps of signing in with two-step: none of them signs the person in; finishSignIn does, once the sign-in page is done with them
  const twoFactorCall = async (path, body, email) => {
    try {
      const response = await axios.post(`/api/auth/2fa/${path}`, body);
      if (response.data?.trustedBrowser) keepTrusted(email, response.data.trustedBrowser);
      return { success: true, data: response.data };
    } catch (error) {
      const data = error.response?.data;
      return { success: false, error: typeof data === 'string' && data ? data : 'Something went wrong. Please try again.', status: error.response?.status };
    }
  };
  const verifyTwoFactor = (challenge, code, rememberBrowser, email) =>
    twoFactorCall('verify', { challenge, code, rememberBrowser }, email);
  const startTwoFactorSetup = (challenge) => twoFactorCall('enroll/start', { challenge });
  const finishTwoFactorSetup = (challenge, code, rememberBrowser, email) =>
    twoFactorCall('enroll/finish', { challenge, code, rememberBrowser }, email);
  const finishSignIn = ({ token, user: userData }) => {
    applySession(token, userData);
    setUser(userData);
    return userData;
  };

  const switchOrganization = async (organizationId) => {
    try {
      const response = await axios.post('/api/auth/switch-organization', { organizationId });
      const { token, user: userData } = response.data;
      applySession(token, userData);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Failed to switch organization'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Registration failed',
        status: error.response?.status
      };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post('/api/auth/verify-email', { email, code });
      return {
        success: true,
        message: response.data?.message || 'Email verified successfully.'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Unable to verify email'
      };
    }
  };

  const resendVerificationCode = async (email) => {
    try {
      const response = await axios.post('/api/auth/resend-verification-code', { email });
      return {
        success: true,
        message: response.data?.message || 'If your account needs verification, a new code has been sent.'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Unable to resend verification code'
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      return {
        success: true,
        message: response.data?.message || 'If an account exists for that email, a password reset link has been sent.'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Unable to send reset link'
      };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await axios.post('/api/auth/reset-password', { token, password });
      return {
        success: true,
        message: response.data?.message || 'Password has been reset successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Unable to reset password'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Tenant-ID'];
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    verifyEmail,
    resendVerificationCode,
    forgotPassword,
    resetPassword,
    logout,
    switchOrganization,
    verifyTwoFactor,
    startTwoFactorSetup,
    finishTwoFactorSetup,
    finishSignIn,
    dateFormat,
    expiryEnabled,
    priceListsEnabled,
    orgSettingsLoaded,
    storeEnabled,
    storeNewOrders,
    refreshStore,
    refreshDateFormat,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
